import React, { useEffect, useState } from 'react';

import invariant from 'invariant';
import type { CombinedError, UseQueryState } from 'urql';

import { isDevelopment } from '@watershed/config/env';
import FailedToContactApiDevelopmentErrorBox from '@watershed/shared-frontend/components/FailedToContactApiDevelopmentErrorBox';
import LoadingPage from '@watershed/shared-frontend/components/LoadingPage';
import Redirect from '@watershed/shared-frontend/components/Redirect';
import isFetchingOrPartial from '@watershed/shared-frontend/utils/isFetchingOrPartial';
import { useQueryParam } from '@watershed/shared-frontend/utils/queryParamHooks';
import { routeForLogin } from '@watershed/shared-universal/dashboardRoutes';
import useGlobalLocation from '@watershed/ui-core/hooks/useGlobalLocation';

import {
  GQActiveOrganizationFieldsFragment,
  GQActiveOrganizationQuery,
  GQFlagsInitFieldsFragment,
  GQForceRedirectQuery,
} from '@watershed/app-dashboard/generated/graphql-operations';
import {
  useActiveOrganizationQuery,
  useForceRedirectQuery,
} from '@watershed/app-dashboard/generated/urql';

import SharedAppContainer from '../components/SharedAppContainer';
import { ORG_ID_PARAM } from '@watershed/shared-universal/utils/pinnedOrganizationUniversalConstants';
import {
  getPinnedOrganizationId,
  initPinnedOrganizationId,
  switchPinnedOrganizationId,
  clearPinnedOrganizationId,
} from '@watershed/shared-frontend/utils/pinnedOrganizationId';
import { captureException } from '@sentry/browser';
import { getTeamInfo, Teams } from '@watershed/constants/teams';
import { ViewerNoPermissionPage } from '../components/NotFoundPage';
import { getOrgIdParamAction } from './utils/orgIdParamHandler';
import assertNever from '@watershed/shared-util/src/assertNever';

type ActiveOrganizationState =
  | { state: 'loading' }
  | { state: 'redirecting'; redirectPath: string }
  | {
      state: 'error';
      error: CombinedError;
    }
  | {
      state: 'loaded';
    };

function getActiveOrganizationState(
  result: UseQueryState<GQActiveOrganizationQuery>,
  forceRedirectResult: UseQueryState<GQForceRedirectQuery>
): ActiveOrganizationState {
  if (isFetchingOrPartial(result) || isFetchingOrPartial(forceRedirectResult)) {
    return { state: 'loading' };
  }
  // Note: This logic is duplicative to the logic in `errorExchange`,
  // and that logic & redirect often happens before this happens.
  if (result.error?.response?.status === 401) {
    const pathAndQuery = window.location.toString().replace(window.origin, '');
    return {
      state: 'redirecting',
      redirectPath: routeForLogin({ redirect: pathAndQuery }),
    };
  }
  const forceRedirect = forceRedirectResult.data?.forceRedirect;
  if (forceRedirect && !window.location.pathname.startsWith(forceRedirect)) {
    return { state: 'redirecting', redirectPath: forceRedirect };
  }
  if (result.error) {
    return { state: 'error', error: result.error };
  }
  return {
    state: 'loaded',
  };
}

type FetchActiveOrganizationResult = {
  activeOrganization: GQActiveOrganizationFieldsFragment;
  flags: Array<GQFlagsInitFieldsFragment>;
  preferredLocale: string | null;
};

// Handles logic around org id params in urls, initializes session storage org id, then renders the children.
export function ActiveOrganizationData({
  activeOrganizationData,
  children,
}: {
  activeOrganizationData: FetchActiveOrganizationResult;
  children: (
    activeOrganizationQuery: FetchActiveOrganizationResult
  ) => React.ReactNode;
}) {
  const [urlOrgId] = useQueryParam(ORG_ID_PARAM);
  const pinnedOrganizationId = getPinnedOrganizationId();

  const orgId = activeOrganizationData.activeOrganization.id;
  const accessibleOrgs =
    activeOrganizationData.activeOrganization.user.accessibleOrgs;
  // Check if there's an org id in the url, and if so, we may need to switch orgs.
  const orgIdParamAction = getOrgIdParamAction({
    urlOrgId,
    currentOrgId: orgId,
    accessibleOrgs: accessibleOrgs.map((org) => ({
      id: org.id,
      canAccessInCurrentSession: org.canAccessInCurrentSession,
    })),
    currentUrl: window.location.href,
  });

  switch (orgIdParamAction.type) {
    case 'no-permission':
      return <ViewerNoPermissionPage orgName={orgId} />;

    case 'switch-org':
      switchPinnedOrganizationId({
        orgId: orgIdParamAction.targetOrgId,
        shouldOpenInNewTab: false,
        redirect: orgIdParamAction.redirect,
      });
      return <LoadingPage />;

    case 'switch-org-reauth-required':
      return <Redirect to={orgIdParamAction.url} />;

    case 'clean-url':
      window.history.replaceState(
        window.history.state,
        '',
        orgIdParamAction.redirect
      );
      break;

    case 'none':
      // No action needed
      break;
    default:
      assertNever(orgIdParamAction);
  }

  // This is very important! We initialize the pinned org on page load so that
  // the user can have multiple tabs open with different orgs.
  if (pinnedOrganizationId === null) {
    initPinnedOrganizationId(orgId);
  } else if (pinnedOrganizationId !== orgId) {
    // It's possible that the pinned org id is not the same as the org id in the active organization data, probably
    // in login-as scenarios. In that case, clear the pinned org id so it resets to the one on the active organization.
    console.warn(
      `ERROR: initOrganizationId was called with inconsistent ids - {orgId: ${orgId} vs ${pinnedOrganizationId}}`
    );
    captureException(
      new Error(
        `existing problem: initOrganizationId was called with inconsistent ids - {orgId: ${orgId} vs ${pinnedOrganizationId}}`
      ),
      {
        tags: {
          owner: getTeamInfo(Teams.ApplicationPlatform).sentryTeam,
        },
      }
    );
    clearPinnedOrganizationId();
  }

  return <>{children(activeOrganizationData)}</>;
}

export function FetchActiveOrganizationQuery({
  children,
}: {
  children: (
    activeOrganizationQuery: FetchActiveOrganizationResult
  ) => React.ReactNode;
}) {
  const { location } = useGlobalLocation();
  const [loginAsRedirect] = useQueryParam('loginAsRedirect');

  // Fetch active organization query
  const [result] = useActiveOrganizationQuery();
  const { data: activeOrganizationData, error: activeOrganizationError } =
    result;
  // Fetch force redirect query
  const [forceRedirectValidated, setForceRedirectValidated] = useState(false);

  const fromDocs = new URLSearchParams(location.search).get('from') === 'docs';
  // Because pathname is a variable here, when the location changes, we refetch the force redirect query.
  const [forceRedirectResult] = useForceRedirectQuery({
    variables: { pathname: location.pathname, fromDocs },
    pause: forceRedirectValidated,
  });

  // Figure out state based on active org and force redirect
  const activeOrganizationState = getActiveOrganizationState(
    result,
    forceRedirectResult
  );

  useEffect(() => {
    if (!forceRedirectValidated && forceRedirectResult.data) {
      // If we were force-redirected (i.e. result of forceRedirect query is not null) we want subsequent navigations to
      // check the force redirect hence we invalidate the pause.
      // This triggers a re-fetch of the force redirect query when the user navigates to a new page.
      setForceRedirectValidated(!forceRedirectResult.data.forceRedirect);
    }
  }, [forceRedirectValidated, forceRedirectResult.data]);

  switch (activeOrganizationState.state) {
    case 'loading':
      return <LoadingPage />;
    case 'redirecting':
      const redirectUrl = new URL(
        activeOrganizationState.redirectPath,
        window.location.origin
      );

      // When someone logs in to dashboard from admin (using login-as), we
      // broadcast a message to all browser tabs with an active dashboard
      // session, which then shows a warning if the session's active org is
      // different from the org the user just logged in to. This logic depends on
      // having `loginAsRedirect` param set to true. Therefore we should preserve
      // the param when we force redirect. See useLoginAsCrossOrgWarning.
      if (loginAsRedirect === 'true') {
        redirectUrl.searchParams.set('loginAsRedirect', 'true');
      }

      return <Redirect to={`${redirectUrl.pathname}${redirectUrl.search}`} />;
    case 'error':
      if (isDevelopment()) {
        return (
          <FailedToContactApiDevelopmentErrorBox
            error={activeOrganizationState.error}
          />
        );
      }
      throw new Error(
        `Failed to load activeOrganization query. Error: ${activeOrganizationError?.message}`
      );
    case 'loaded':
      invariant(
        activeOrganizationData,
        'active organization data not present when state is loaded'
      );
      return (
        <ActiveOrganizationData activeOrganizationData={activeOrganizationData}>
          {children}
        </ActiveOrganizationData>
      );
    default:
      assertNever(activeOrganizationState);
  }
}

/**
 * The logged in layout is the outermost layout component for the dashboard.
 * It is responsible for fetching the active organization and providing it
 * to the shared app container.
 *
 * Notably, this is an important place to put any top level redirects because
 * at this point we have not initiated any downstream queries that might fail
 * if we have cross-org issues or need to redirect a user based on permissions.
 */
export default function LoggedInLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <FetchActiveOrganizationQuery>
      {(props) => (
        <SharedAppContainer {...props}>{children}</SharedAppContainer>
      )}
    </FetchActiveOrganizationQuery>
  );
}
